/**
 * 콜랩 상세 계측 — 예전 2.0 웹 상세와 **같은 이벤트 이름 · 같은 속성 키**(`Collab Viewed` ·
 *   `Collab App Opened` · `Collab Shared`).
 * @description 대시보드에서 한 퍼널로 읽히도록 이름을 가르지 않는다. `version`(V2 · V3)을 실어 콜랩
 *   종류를 나눠 볼 수 있게 했다 — 이 키가 없는 예전 이벤트는 2.0 웹 상세에서 난 것이다.
 *
 *   `collabStatus` 는 3.0 화면 상태(PUBLISHED · ENDED · CANCELLED)라 예전 2.0 값(RECRUITMENT …)과
 *   어휘가 다르다. `statusBadge` 는 그보다 잘게 가른 배지(GATHERING · IN_PROGRESS …)다.
 *   웹에서 참여 · 신청이 없어져 `Collab Joined` · `Collab Applied` 는 더 나지 않는다.
 */
import type { CollabV3Version } from "./dtos";
import type { CollabV3Detail } from "./entities";
import { trackEvent } from "@/shared/lib/analytics/amplitude";
import { referrers } from "@/shared/lib/analytics/referrers";
import type { ShareOutcome } from "@/shared/lib/share";

/** 이벤트마다 같은 모양으로 붙는 콜랩 속성 — 서버가 만들어 클라이언트에 넘긴다 */
export interface CollabV3EventSubject {
  collabId: number;
  version: CollabV3Version;
  collabStatus: string;
  statusBadge: string;
  isOnline: boolean;
  artistId?: number;
}

/**
 * 앱으로 넘어가는 자리 — 하단 버튼, 참여자 줄, 작품(칸 · 더 보기), 스레드(글 · 더 보기).
 * @description 웹은 보기 전용이라 참여 · 결제 · 글쓰기 자리를 두지 않고 앱으로 가는 길만 둔다.
 */
export type CollabV3AppOpenPlacement =
  | "cta"
  | "participants"
  | "works"
  | "thread";

export const toCollabV3EventSubject = (
  detail: CollabV3Detail,
): CollabV3EventSubject => ({
  collabId: detail.id,
  version: detail.version,
  collabStatus: detail.status,
  statusBadge: detail.badge,
  isOnline: detail.place?.isOnline ?? false,
  artistId: detail.artist?.id,
});

export function trackCollabV3Viewed(
  subject: CollabV3EventSubject,
  isGuest: boolean,
): void {
  trackEvent("Collab Viewed", { ...subject, ...referrers(), isGuest });
}

export function trackCollabV3AppOpened(
  subject: CollabV3EventSubject,
  placement: CollabV3AppOpenPlacement,
): void {
  trackEvent("Collab App Opened", { ...subject, placement });
}

export function trackCollabV3Shared(
  subject: CollabV3EventSubject,
  outcome: ShareOutcome,
): void {
  trackEvent("Collab Shared", { ...subject, placement: "header", outcome });
}
